import { Typography, Stack, TableHead, TableCell, TableRow, Table, TableBody, Tooltip, Button, TableContainer, Checkbox, ButtonGroup, Select, MenuItem, Chip, TextField, CircularProgress, InputAdornment } from '@mui/material';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Loading from '../components/Loading';
import { useGetOrderLineitemsQuery, useListOrdersQuery, useUpdateOrderMutation } from '../features/model/orderSlice';
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc';
import { storeImage, storeName } from '../helpers/stores';
import { mapStatus, PRIORITIES } from '../helpers/statuses';
import { useListAssigneesQuery } from '../features/assigneeSlice';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDebounce } from "../helpers/debounce";
import { CheckCircle, Circle, SwapVerticalCircleOutlined } from '@mui/icons-material';

dayjs.extend(utc);

let USD = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
});

const lcofields = !(process.env.REACT_APP_PREFIX && process.env.REACT_APP_PREFIX.includes("lcc"));
const lccfields = !lcofields;

const PrioritySelector = ({object}) => {
    const [updateOrder, {isLoadingUpdate: isUpdatingOrder }] = useUpdateOrderMutation();

    const handleChange = (event) => {
        let newPriority = parseInt(event.target.value);
        updateOrder({order_id: object.id, data: {
            ...object.data,
            priority: newPriority
        }})
    };

    return <Select
    variant="standard"
    size="small"
        labelId="priority-select-label"
        id="priority-select"
        value={object.priority}
        onChange={handleChange}
    >
        {PRIORITIES.map((priority) => (
            <MenuItem key={priority.value} value={priority.value}>
                <span style={{color: priority.color}}>{priority.label}</span>
            </MenuItem>
        ))}
    </Select>;
};

const AssigneeSelector = ({object, assignees = []}) => {
    const [updateOrder, {isLoadingUpdate: isUpdatingOrder }] = useUpdateOrderMutation();
    const currentAssignees = object.assignee ? object.assignee.split(",") : [];

    const generateHexColor = (str) => {
        let colors = [];
        for (let i = 0; i < str.length; i++) {
            let hash = 0;
            for (let j = 0; j < str.length; j++) {
                hash = str.charCodeAt(j) + ((hash << 5) - hash);
            }
            let color = '#';
            for (let j = 0; j < 3; j++) {
                let value = (hash >> (j * 8)) & 0xFF;
                color += ('00' + value.toString(16)).substr(-2);
            }
            colors.push(color);
        }
        return colors;
    }

    const assigneeColor = (assignee) => {
        let index = assignees.indexOf(assignee);
        let colors = generateHexColor(assignee);
        return colors[index % colors.length];
    }
    const handleChange = (event) => {
        let newAssignee = event.target.value;
        console.log(currentAssignees, newAssignee);
        if (!currentAssignees.includes(newAssignee)) {
            updateOrder({order_id: object.id, data: {
                ...object.data,
                assignee: [...currentAssignees, newAssignee].join(",")
            }})
        }
    };
    return <div>
        <Select
            variant="standard"
            size="small"
            labelId="assignee-select-label"
            id="assignee-select"
            value={""}
            onChange={handleChange}
        >
            {assignees.map((assignee) => (
                <MenuItem key={assignee} value={assignee}>
                    <Chip style={{
                        backgroundColor: assigneeColor(assignee),
                        color: "#ffffff",
                        fontWeight: "bold"
                    }} key={assignee} label={assignee} />
                </MenuItem>
            ))}
        </Select>
        {currentAssignees.map((assignee) => <Chip style={{
            backgroundColor: assigneeColor(assignee),
            color: "#ffffff",
            fontWeight: "bold"
        }} key={assignee} label={
            <div style={{display: "flex", flexDirection: "row", alignItems: "center", gap: "4px"}}>
                {assignee}
                <DeleteIcon fontSize="small" onClick={() => {
                    let newAssignees = currentAssignees.filter(a => a !== assignee);
                    updateOrder({order_id: object.id, data: {
                        ...object.data,
                        assignee: newAssignees.join(",")
                    }})
                }}/>
            </div>
            }
        />)}
    </div>;
}

const NowWorkingOnField = ({object}) => {
    const [updateOrder, {isLoadingUpdate: isUpdatingOrder, isSuccess }] = useUpdateOrderMutation();
    const nowWorkingOn = object.nwo ?? "";
    const [updating, setUpdating] = useState(false);
    const [value, setValue] = useState(nowWorkingOn);
    const debouncedValue = useDebounce(value, 1500);

    useEffect(() => {
        if (debouncedValue !== nowWorkingOn && !isUpdatingOrder && debouncedValue !== undefined) {
            setUpdating(true);
            updateOrder({order_id: object.id, data: {
                nwo: debouncedValue
            }}).then(() => {
                setTimeout(() => setUpdating(false), 500);
            })
        }
    }, [debouncedValue, nowWorkingOn, isUpdatingOrder, object, updateOrder]);
    
    return <div>
        <TextField
            variant="standard"
            size="small"
            fullWidth
            label=""
            placeholder="..."
            value={value}
            onChange={(e) => {
                setValue(e.target.value)
            }}
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        { updating && <SwapVerticalCircleOutlined color="primary" size={12} /> }
                        { isSuccess && !updating && <CheckCircle color="primary" size={12} />}
                        { !isSuccess && !updating && <Circle size={12} style={{opacity: 0}} />}
                    </InputAdornment>
                ),
            }}
        />
    </div>;
};

const OrderHeader = ({rep_data=false, flowFields=true}) => {
    let details = []
    if (rep_data) {
        details.push("Order total")
        details.push("Commission")
        details.push("Paid")
        details.push("Paid date")
    }
    return <TableHead><TableRow>
        <TableCell>Order #</TableCell>
        <TableCell>Date</TableCell>
        <TableCell>Recipient</TableCell>
        { lcofields && <><TableCell>Store</TableCell>
        <TableCell>Items</TableCell>
        <TableCell>Status</TableCell>
        </>
        }
        { flowFields && (<>
            <TableCell>Priority</TableCell>
            <TableCell>Assignees</TableCell>
          </>)
        }
        { lccfields && !rep_data && <><TableCell>Now Working On</TableCell>
        </>
        }
        {
            details.map((detail, i) => <TableCell key={`detail${i}`}>{detail}</TableCell>)
        }</TableRow>
    </TableHead>;
}

const OrderListRow = ({object, assignees=[], rep_data=null, rep_admin=false}) => {
  const [updateOrder, {isLoadingUpdate: isUpdatingOrder }] = useUpdateOrderMutation();
  
  const makeLinkToObject = (object) => {
    let md = JSON.parse(object.metadata);
    if(Object.keys(md).includes("draft_po_data")) {
        let draft_id = md.draft_po_data.draft_id;
        return `/orders/${object.id}/po?draft_id=${draft_id}`
    }
    return `/orders/${object.id}`
  }


  let metadata = JSON.parse(object.metadata);
  let summary = metadata.summary ? metadata.summary : "";

  return <TableRow key={`order_${object.id}`}>
    <TableCell><Button variant="outlined" component={RouterLink} to={makeLinkToObject(object)} color={object.status === 'rush' ? "warning" : "primary"}><Typography noWrap>{object.order_id}</Typography></Button></TableCell>
    <TableCell><Tooltip title={dayjs.utc(object.received).local().format('YYYY-MM-DD HH:mm:ss')}><Typography noWrap>{dayjs.utc(object.received).local().format('M/D/YY')}</Typography></Tooltip></TableCell>
    <TableCell>{object.recipient_name}</TableCell>
    { lcofields && <>
    <TableCell><div style={{alignItems: "center", display: "flex"}}>{storeImage(object.store.type)} {storeName(object.store.name)}</div></TableCell>
    <TableCell><Stack direction="column">{summary.split("\n").map((line, i) => <Typography key={`caption${i}`} variant="caption">{line.replace(" x0", "")}</Typography>)}</Stack></TableCell>
    <TableCell><Typography noWrap variant="caption">{mapStatus(object.status)}</Typography></TableCell>
    </>}
    <TableCell><Typography noWrap variant="caption"><PrioritySelector object={object} /></Typography></TableCell>
    <TableCell><Typography noWrap variant="caption"><AssigneeSelector object={object} assignees={assignees} /></Typography></TableCell>
    <TableCell><Typography noWrap variant="caption"><NowWorkingOnField object={object} /></Typography></TableCell>
  </TableRow>
}

function findWidth(custom) {
    for (const [key, value] of Object.entries(custom)) {
        let k = key.toLowerCase()
        if (k.includes("door width") || k.includes("front width") || k.includes("toe kick width") || k.includes("panel width")) {
            return parseFloat(value)
        }
    }
    return 0.0
}

function findHeight(custom) {
    for (const [key, value] of Object.entries(custom)) {
        let k = key.toLowerCase()
        if (k.includes("door height") || k.includes("front height") || k.includes("toe kick height") || k.includes("panel height")) {
            return parseFloat(value)
        }
    }
    return 0.0
}

function commissionForOrder(order, contracts, lineitems) {
    if(!contracts) {
        return 0.0
    }
    var contractObjects = contracts.slice().filter((c) => dayjs(order.received).isBetween(dayjs.utc(c.startdate), dayjs.utc(c.enddate), null, "[]")).sort((a, b) => a.commission > b.commission);
    if(contractObjects.length > 0) {
        let bestContract = contractObjects[0]
        var shippingCost = 0.0
        for(var i=0;i<lineitems.length;i++) {
            let custom_str = lineitems[i].customization
            if(custom_str){
                let custom = JSON.parse(custom_str)
                if("Local Pickup" in custom) {
                    if(custom["Local Pickup"] !== "Yes") {
                        let w = findWidth(custom)
                        let h = findHeight(custom)
                        let sqft = w * h / 144.0
                        shippingCost += 5.5 * sqft * lineitems[i].quantity
                    }
                }
            }
        }
        return order.total_excluding_tax * (bestContract.commission / 100.0) - shippingCost
    }
    return 0.0
}

function commission_paid_for_rep(order, rep) {
    var order_meta = JSON.parse(order.metadata);
    if (order_meta.commission_data) {
        return `${rep.id}` in order_meta.commission_data;
    }
    return false;
}

function commission_paid_for_rep_at(order, rep) {
    var order_meta = JSON.parse(order.metadata);
    if (order_meta.commission_data_stamp) {
        let ret = order_meta.commission_data_stamp[`${rep.id}`];
        return ret ? dayjs.utc(ret).local().format("M/D/YY") : ""
    }
    return "";
}

function mark_commission_paid_for_rep(order, rep, value) {
    var order_meta = JSON.parse(order.metadata);
    if (order_meta.commission_data) {
        if (value) {
            order_meta.commission_data[`${rep.id}`] = true;
            order_meta.commission_data_stamp[`${rep.id}`] = dayjs.utc().format();
        } else {
            delete order_meta.commission_data[`${rep.id}`];
            if (order_meta.commission_data_stamp) {
                delete order_meta.commission_data_stamp[`${rep.id}`];
            }
        }
    } else {
        var commission_dict = {};
        commission_dict[`${rep.id}`] = value;
        order_meta.commission_data = commission_dict;
        var commission_dict_stamp = {};
        commission_dict_stamp[`${rep.id}`] = dayjs.utc().format();
        order_meta.commission_data_stamp = commission_dict_stamp;
    }
    return JSON.stringify(order_meta);
}

const SalesRepOrderListRow = ({object, contracts, rep_data=null, rep_admin=false}) => {
    const [updateOrder, {isLoadingUpdate: isUpdatingOrder }] = useUpdateOrderMutation();
    const {data: lineitemdata, error, isLoading } = useGetOrderLineitemsQuery(object.id);

    const makeLinkToObject = (object) => {
        let md = JSON.parse(object.metadata);
        if(Object.keys(md).includes("draft_po_data")) {
            let draft_id = md.draft_po_data.draft_id;
            return `/orders/${object.id}/po?draft_id=${draft_id}`
        }
        return `/orders/${object.id}`
    }
    let details = []
    details.push(<div key="total">{USD.format(object.total_excluding_tax)}</div>)
    var commission = "..."
    if(!error && !isLoading && lineitemdata) {
        commission = commissionForOrder(object, contracts, lineitemdata.objects)
    }
    if(object.status !== "draft") {
        details.push(<div key="commission">{USD.format(commission)}</div>)
        if(rep_admin) {
            details.push(<Checkbox key="commission_paid" checked={commission_paid_for_rep(object, rep_data)} onChange={(e) => {
                let new_status = !(commission_paid_for_rep(object, rep_data));
                updateOrder({order_id: object.id, data: {
                    ...object.data,
                    "metadata": mark_commission_paid_for_rep(object, rep_data, new_status),
                    "commission_paid": new_status,
                    "commission_paid_at": new_status ? dayjs.utc().format() : null
                }})
            }}/>)
        } else {
            details.push(<div key="commission_paid">{commission_paid_for_rep(object, rep_data) ? "paid" : "-"}</div>)
        }
        details.push(<div key="commission_paid_at">{commission_paid_for_rep_at(object, rep_data)}</div>)
    } else {
        details.push(<div></div>);
        details.push(<div></div>);
        details.push(<div></div>);
    }
    let metadata = JSON.parse(object.metadata);
    let summary = metadata.summary ? metadata.summary : "";

    return <TableRow key={`order_${object.id}`}>
      <TableCell><Button variant="outlined" component={RouterLink} to={makeLinkToObject(object)}><Typography noWrap>{object.order_id}</Typography></Button></TableCell>
      <TableCell><Tooltip title={dayjs.utc(object.received).local().format('YYYY-MM-DD HH:mm:ss')}><Typography noWrap>{dayjs.utc(object.received).local().format('M/D/YY')}</Typography></Tooltip></TableCell>
      <TableCell><div style={{alignItems: "center", display: "flex"}}>{storeImage(object.store.type)} {storeName(object.store.name)}</div></TableCell>
      <TableCell>{object.recipient_name}</TableCell>
      <TableCell><Stack direction="column">{summary.split("\n").map((line, i) => <Typography variant="caption">{line.replace(" x0", "")}</Typography>)}</Stack></TableCell>
      <TableCell><Typography noWrap variant="caption">{object.status}</Typography></TableCell>
      {
          details.map((detail, i) => <TableCell>{detail}</TableCell>)
      }
    </TableRow>
  }

const OrdersListView = ({filters}) => {
    const [page, setPage] = useState(1);
    
    let query_filters = { ...{ordering: "priority,-received", limit: "100", page: `${page}`}, ...filters };
    const {data, error, isLoading } = useListOrdersQuery(query_filters);
    const { data: assigneeData } = useListAssigneesQuery();

    useEffect(() => {
        if(page > 1) setPage(1);
    }, [filters]);

    if (isLoading) {
      return <Loading />;
    }
    if (error || !data) {
      return <div>Error: {error}</div>
    }

    const assignees = assigneeData?.objects ?? [];

    const sortedObjects = data.objects;

    return <Stack direction="column">
        <ButtonGroup>
            <Button disabled={data.meta.previous === ""} onClick={() => {setPage(page-1);}}>&lt;</Button>
            <Button variant="outlined"  onClick={() => {setPage(1);}}>Page {data.meta.page}</Button>
            <Button disabled={data.meta.next === ""} onClick={() => {setPage(page+1);}}>&gt;</Button>
        </ButtonGroup>
        <TableContainer><Table spacing={2} sx={{marginTop: 2, marginBottom: 4}}>
        <OrderHeader />
        <TableBody>
        {
            sortedObjects.map((object, i) => <OrderListRow object={object} key={object.id} assignees={assignees} />)
        }
        </TableBody>
        </Table></TableContainer></Stack>
}

const SalesRepOrdersListView = ({salesrep, contracts, discounts, admin=false}) => {
    const [page, setPage] = useState(1);

    let query_filters = {ordering: "-received", limit: "100", page: `${page}`, discount__in: (discounts.length > 0 ? discounts.join(",") : "-1")};
    const {data, error, isLoading } = useListOrdersQuery(query_filters);

    if (isLoading) {
      return <Loading />;
    }
    if (error || !data) {
      return <></>;
    }

    if (!data.objects) {
        return <></>;
    }

    const sortedObjects = data.objects.slice().sort((a, b) => b.received.localeCompare(a.received));

    return <Stack direction="column">
        <ButtonGroup>
            <Button disabled={data.meta.previous === ""} onClick={() => {setPage(page-1);}}>&lt;</Button>
            <Button variant="outlined"  onClick={() => {setPage(1);}}>Page {data.meta.page}</Button>
            <Button disabled={data.meta.next === ""} onClick={() => {setPage(page+1);}}>&gt;</Button>
        </ButtonGroup>
        <TableContainer><Table spacing={2} sx={{marginTop: 2, marginBottom: 4}}>
        <OrderHeader rep_data={true} flowFields={false} />
        {
            sortedObjects.map((object, i) => <SalesRepOrderListRow object={object} key={object.id} contracts={contracts} rep_data={salesrep} rep_admin={admin} />)
        }
        </Table></TableContainer></Stack>;
}

export { OrdersListView, SalesRepOrdersListView };